import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProgressIndicator from './components/ProgressIndicator';
import AccountCreationForm from './components/AccountCreationForm';
import EmailVerificationForm from './components/EmailVerificationForm';
import InvestmentProfileForm from './components/InvestmentProfileForm';
import SocialRegistration from './components/SocialRegistration';
import TrustSignals from './components/TrustSignals';

const Register = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    agreeToPrivacy: false,
    marketingEmails: false,
    experienceLevel: '',
    riskTolerance: '',
    investmentGoals: []
  });

  const totalSteps = 3;

  const handleNextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleRegistrationComplete = () => {
    // Simulate successful registration
    setTimeout(() => {
      navigate('/dashboard');
    }, 1000);
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <AccountCreationForm
            onNext={handleNextStep}
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 2:
        return (
          <EmailVerificationForm
            onNext={handleNextStep}
            onBack={handlePrevStep}
            formData={formData}
          />
        );
      case 3:
        return (
          <InvestmentProfileForm
            onComplete={handleRegistrationComplete}
            onBack={handlePrevStep}
            formData={formData}
            setFormData={setFormData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container-financial py-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Column - Form */}
            <div className="order-2 lg:order-1">
              <div className="max-w-md mx-auto lg:mx-0">
                {/* Header */}
                <div className="text-center lg:text-left mb-8">
                  <h1 className="text-3xl font-bold text-foreground mb-2">
                    Create Your Account
                  </h1>
                  <p className="text-muted-foreground">
                    Join thousands of investors building their financial future
                  </p>
                </div>

                {/* Progress Indicator */}
                <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />

                {/* Step Content */}
                <div className="bg-card rounded-lg border p-6 mb-6">
                  {renderStepContent()}
                </div>

                {/* Social Registration - Only show on first step */}
                {currentStep === 1 && (
                  <div className="bg-card rounded-lg border p-6 mb-6">
                    <SocialRegistration />
                  </div>
                )}

                {/* Login Link */}
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{' '}
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleLoginRedirect}
                      className="p-0 h-auto font-medium"
                    >
                      Sign in here
                    </Button>
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Educational Content & Trust Signals */}
            <div className="order-1 lg:order-2">
              <div className="space-y-8">
                {/* Platform Benefits */}
                <div className="bg-card rounded-lg border p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-4">
                    Why Choose StockTracker Pro?
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="BarChart3" size={16} className="text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">Real-Time Portfolio Tracking</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Monitor your investments with live market data and instant updates
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="PieChart" size={16} className="text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">Advanced Analytics</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Get detailed insights with interactive charts and performance metrics
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Search" size={16} className="text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">Smart Stock Discovery</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Find new investment opportunities with our AI-powered recommendations
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Smartphone" size={16} className="text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">Mobile Optimized</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Access your portfolio anywhere with our responsive design
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trust Signals */}
                <div className="bg-card rounded-lg border p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-4">
                    Trusted & Secure
                  </h3>
                  <TrustSignals />
                </div>

                {/* Testimonial */}
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="Quote" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-foreground mb-3">
                        "StockTracker Pro has completely transformed how I manage my investments. 
                        The real-time insights and easy-to-use interface make it perfect for both 
                        beginners and experienced investors."
                      </p>
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                          <Icon name="User" size={14} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">Sarah Johnson</p>
                          <p className="text-xs text-muted-foreground">Portfolio Manager</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Market Stats */}
                <div className="bg-card rounded-lg border p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Join Our Growing Community
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">500K+</div>
                      <div className="text-sm text-muted-foreground">Active Investors</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">₹207.5B+</div>
                      <div className="text-sm text-muted-foreground">Assets Tracked</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">99.9%</div>
                      <div className="text-sm text-muted-foreground">Uptime</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">4.8★</div>
                      <div className="text-sm text-muted-foreground">User Rating</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;